import { OutOfCoreTaskBase } from "../OutOfCoreTaskBase";
import { INVALID_GEOM_ID, USE_GEOMETRY_OOC } from "../../../../globals";
import { MeshFlags } from "../../../MeshFlags";

/** @import {BvhNode} from "../../BvhNode.js" */

/**
 * @typedef {string} ResourceHash - Hash type used to identify geometries and materials in the cache
 */

/**
 * Interface that a loader must implement to work with LoadGeometryTask.
 * Used by the out-of-core tile manager to load and unload geometry on demand.
 *
 * @typedef {Object} IGeometryLoader
 *
 * @property {true} supportsGeometryLoadTask - Used to runtime-detect whether a loader supports this interface (initializeTasks.js)
 *
 * @property {function(number): ResourceHash} getGeometryHash - Get the hash for a geometry ID
 * @property {function(number): number} getMaterialId - Get the material ID for a fragment
 * @property {function(number): ResourceHash} getMaterialHash - Get the hash for a material ID
 * @property {function(ResourceHash): boolean} isMaterialPending - Check if a material is still pending (not yet loaded)
 * @property {function(): Autodesk.Viewing.Private.OtgResourceCache} geomCache - Get the geometry cache used for on-demand loading
 */

/**
 * Note: When using geometry-out-of-core (code path with USE_GEOMETRY_OOC=true), GeometryLoadTask uses additional
 * functions that are not covered by the interface above (loadFragments, unloadFragments). This is okay and intended
 * as long as USE_GEOMETRY_OOC is under development and default off. Once USE_GEOMETRY_OOC is finalized and enabled
 * by default, we need to ensure that the additional functions are supported by loaders as well.
 */

export class LoadGeometryTask extends OutOfCoreTaskBase {

  static #geomRefCounts = new Map();
  static #geometrySizes = new Map();

  /**
   *@param {BvhNode} node The node to process in this task
   */
  constructor(node) {
    super(node.outOfCoreTileManager);

    this.node = node;
  }

  /**
   * Requests the geometry for the node from the loader
   */
  async execute() {
    const node = this.node;
    const model = node.model;
    const fragList = model.getFragmentList();
    const bvhModelIterator = model.getIterator();
    const renderBatch = bvhModelIterator.getGeomScenes()[node.nodeId];
    const renderBatchIndices = renderBatch.getIndices();

    let fragIds;
    if (USE_GEOMETRY_OOC) {
      // Activate the fragments in the loader
      fragIds = renderBatchIndices.slice(renderBatch.start, renderBatch.lastItem);
      model.loader.loadFragments(fragIds, MeshFlags.MESH_SWAPPED_OUT_OF_CORE);
    }

    const missingHashes = new Set();

    const firstRefcountIncrementHashes = new Set();

    for (let i = renderBatch.start; i < renderBatch.lastItem; i++) {

      const fragId = renderBatchIndices[i];

      // TODO If a fragment is set to MESH_NOTLOADED, its geometry will never be loaded.
      // So we shouldn't request this from the OtgResourceCache, and there is nothing to wait for.
      if (fragList.isNotLoaded(fragId)) {
        continue;
      }

      const geomId = fragList.getGeometryId(fragId);
      if (geomId !== INVALID_GEOM_ID) {
        // We have to increase the refcounts before the await below, otherwise geometry
        // that was already loaded and is needed by this node might get removed while we are awaiting more geometry.
        let hash = model.loader.getGeometryHash(geomId);

        if (USE_GEOMETRY_OOC) {
          const currentCount = LoadGeometryTask.#geomRefCounts.get(hash) ?? 0;
          if (currentCount === 0) {
            firstRefcountIncrementHashes.add(hash);
          }
          LoadGeometryTask.#geomRefCounts.set(hash, currentCount + 1);
        }

        if (!fragList.geoms.hasGeometry(geomId)) {
          missingHashes.add(hash);
        }

      }

      // materials are always requested immediately when loading the fraglist, but we still need to wait for them.
      if (!fragList.hasMaterial(fragId)) {
        // The loader might already have the material, but it's not set on the fragment until it's activated.
        // So we have to check pendingMaterials.
        const materialId = model.loader.getMaterialId(fragId);
        const hash = model.loader.getMaterialHash(materialId);
        if (model.loader.isMaterialPending(hash)) {
          missingHashes.add(hash);
        }
      }
    }

    if (missingHashes.size > 0) {
      const geomCache = model.loader.geomCache();
      const priority = node.getCurrentScreenSpaceError(node.getNextStage());
      let success = await geomCache.batchRequest(missingHashes, priority);

      if (!success) {
        console.log(`Failed to fetch all hashes for node ${node.nodeId}`);
      }
    }

    if (USE_GEOMETRY_OOC) {
      model.loader.loadFragments(fragIds, MeshFlags.MESH_SWAPPED_OUT_OF_CORE);

      // Report memory consumed by this task. We have to do this after all the geometry
      // is available, otherwise we might not know all the geometry sizes.
      let consumedMemory = 0;
      const geomCache = model.loader.geomCache();
      for (let hash of firstRefcountIncrementHashes) {
        // why `|| 0` and why write that to #geometrySizes? Because otherwise geometries that failed to
        // load would never appear in #geometrySizes and would continuously force us to use the size heuristic in
        // getResourceCost, resulting in slower convergence.
        const size = geomCache.getGeometry(hash)?.getByteSize() || 0;
        LoadGeometryTask.#geometrySizes.set(hash, size);
        consumedMemory += size;
      }

      this.setResourceAmountConsumedByLastExecution(consumedMemory);
    }
  }

  undo() {
    if (!USE_GEOMETRY_OOC) {
      return;
    }

    const node = this.node;
    const model = node.outOfCoreTileManager.model;
    const fragList = model.getFragmentList();

    const bvhModelIterator = model.getIterator();
    const renderBatch = bvhModelIterator.getGeomScenes()[node.nodeId];
    const renderBatchIndices = renderBatch.getIndices();
    const fragIds = renderBatchIndices.slice(renderBatch.start, renderBatch.lastItem);

    // Report memory freed by this task
    let freedMemory = 0;
    for (let fragId of fragIds) {
      if (fragList.isNotLoaded(fragId)) {
        continue;
      }

      const geomId = fragList.getGeometryId(fragId);
      if (geomId === INVALID_GEOM_ID) continue;

      let hash = model.loader.getGeometryHash(geomId);
      let geometry = fragList.getGeometry(fragId);
      console.assert(geometry);
      let currentCount = LoadGeometryTask.#geomRefCounts.get(hash);
      console.assert(currentCount !== undefined);
      if (currentCount === 1) {
        freedMemory += geometry.getByteSize();
        LoadGeometryTask.#geomRefCounts.delete(hash);
      } else {
        LoadGeometryTask.#geomRefCounts.set(hash, currentCount - 1);
      }
    }
    this.setResourceAmountFreedByLastExecution(freedMemory);

    model.loader.unloadFragments(fragIds, MeshFlags.MESH_SWAPPED_OUT_OF_CORE);

    // make the cache actually remove the geometry
    model.loader.geomCache().cleanup(true);
  }

  getResourceCost() {
    if (!USE_GEOMETRY_OOC) {
      return 0;
    }

    const hashes = new Set();
    let resourceCost = 0;

    const node = this.node;
    const model = node.outOfCoreTileManager.model;
    const fragList = model.getFragmentList();
    const bvhModelIterator = model.getIterator();
    const renderBatch = bvhModelIterator.getGeomScenes()[node.nodeId];
    const renderBatchIndices = renderBatch.getIndices();
    for (let i = renderBatch.start; i < renderBatch.lastItem; i++) {
      const fragId = renderBatchIndices[i];
      if (fragList.isNotLoaded(fragId)) {
        continue;
      }

      if (!fragList.hasGeometry(fragId)) {
        const geomId = fragList.getGeometryId(fragId);
        const hash = model.loader.getGeometryHash(geomId);
        if (!hashes.has(hash)) {
          // Most geometries are much smaller than 20_000 bytes, but we chose to be
          // conservative here, since underestimating the size could result in exceeding
          // the budget, requiring us to unload nodes directly after loading. The downside
          // is that we approach the limit more cautiously (slower).
          let size = LoadGeometryTask.#geometrySizes.get(hash) ?? 20_000;
          resourceCost += size;
          hashes.add(hash);
        }
      }
    }
    return resourceCost;
  }
}